import {
    FacebookTargetingCitySearchResult,
    FacebookTargetingCountryGroupSearchResult,
    FacebookTargetingCountrySearchResult,
    FacebookTargetingLocation,
    FacebookTargetingLocationOptionsWithRadius,
    FacebookTargetingLocationRadius,
    FacebookTargetingLocations,
    FacebookTargetingLocationSearchOption,
    FacebookTargetingLocationSearchResult,
    FacebookTargetingLocationsResponse,
    FacebookTargetingRegionSearchResult,
    FacebookTargetingZIPSearchResult,
} from '../../../../../../../../App.types';
import { fetchFacebookApi, formatSnakeCaseToDisplay, isFacebookError } from '../../../../../../../../helpers';

export const radiusUnitOptions: Array<FacebookTargetingLocationRadius['unit']> = [
    {
        label: 'km',
        value: 'kilometer',
    },
    {
        label: 'ml',
        value: 'mile',
    },
];

export const cityTypes = ['city', 'subcity', 'large_geo_area', 'medium_geo_area', 'small_geo_area', 'neighborhood'];

export function getMinMax(unit: FacebookTargetingLocationRadius['unit']['value']) {
    return {
        min: unit === 'kilometer' ? 17 : 10,
        max: unit === 'kilometer' ? 80 : 50,
    };
}

export function isRadiusValid(radius: FacebookTargetingLocationRadius): boolean {
    const { min, max } = getMinMax(radius.unit.value);
    const { distance } = radius;
    return distance > min && distance < max;
}

export function convertDistance(distance: number, newUnit: FacebookTargetingLocationRadius['unit']['value']) {
    const { min, max } = getMinMax(newUnit);
    const converted = newUnit === 'kilometer' ? Math.floor(distance / 0.62137) : Math.floor(distance * 0.62137);
    return converted < min ? min : converted > max ? max : converted;
}

export function isFacebookTargetingLocationOptionsWithRadius(
    location: FacebookTargetingLocationSearchOption
): location is FacebookTargetingLocationOptionsWithRadius {
    return cityTypes.includes(location.type);
}

export function isFacebookTargetingLocation(l: any): l is FacebookTargetingLocation {
    return (
        l &&
        typeof l !== 'string' &&
        typeof l.key === 'string' &&
        typeof l.name === 'string' &&
        typeof l.type === 'string'
    );
}

export function mapFacebookTargetingLocationsToBody(
    locations: FacebookTargetingLocationSearchOption[]
): FacebookTargetingLocations {
    return {
        country_objects: locations
            .filter((location) => location.type === 'country')
            .map(mapFacebookTargetingLocationOption),
        regions: locations.filter((location) => location.type === 'region').map(mapFacebookTargetingLocationOption),
        cities: locations.filter(isFacebookTargetingLocationOptionsWithRadius).map(mapFacebookTargetingLocationOption),
        zips: locations.filter((location) => location.type === 'zip').map(mapFacebookTargetingLocationOption),
        geo_markets: locations
            .filter((location) => location.type === 'geo_market')
            .map(mapFacebookTargetingLocationOption),
        electoral_districts: locations
            .filter((location) => location.type === 'electoral_district')
            .map(mapFacebookTargetingLocationOption),
        country_groups: locations
            .filter((location) => location.type === 'country_group')
            .map(mapFacebookTargetingLocationOption),
    };
}

function mapFacebookTargetingLocationOption(
    location: FacebookTargetingLocationSearchOption
): FacebookTargetingLocation {
    const mapped: FacebookTargetingLocation = {
        key: location.value,
        type: location.type,
        name: location.name,
        country_code: location.country_code,
    };

    if (location.region) {
        mapped.region = location.region;
    }

    if (isFacebookTargetingLocationOptionsWithRadius(location)) {
        mapped.radius = location.radius.distance;
        mapped.distance_unit = location.radius.unit.value;
    }

    return mapped;
}

export async function mapFacebookTargetingLocationsToOptions(
    locations?: FacebookTargetingLocationsResponse,
    requestInit?: RequestInit
): Promise<FacebookTargetingLocationSearchOption[]> {
    if (!locations) {
        return [];
    }

    const locationsArray = Object.values(locations).flat();

    // locations that have display data
    const locationOptions = locationsArray.filter(isFacebookTargetingLocation).map((location) => {
        const { region = '', country_code = '' } = location;
        const mapped: FacebookTargetingLocationSearchOption = {
            value: location.key,
            type: location.type,
            name: location.name,
            region: region,
            country_code: country_code,
            label: [location.name, region, country_code].filter((value) => !!value).join(', '),
            isIncluded: true,
            typeLabel: formatSnakeCaseToDisplay(location.type),
            radius: undefined,
        };

        if (location.radius && location.distance_unit && isFacebookTargetingLocationOptionsWithRadius(mapped)) {
            mapped.radius = {
                distance: location.radius,
                unit: radiusUnitOptions.find((o) => o.value === 'kilometer')!,
            };
        }

        return mapped;
    });

    // get locations that don't have display data (key only locations)
    const locationsToFetch = Object.keys(locations).reduce(
        (acc, key) => {
            // @ts-ignore
            acc[key] = acc[key].filter((location) => !isFacebookTargetingLocation(location));
            return acc;
        },
        { ...locations }
    );

    let fetched: FacebookTargetingLocationSearchOption[] = [];
    if (shouldFetchLocationsFromFacebook(locationsToFetch)) {
        fetched = await mapLocationsToOptionsFromFacebook(locationsToFetch, requestInit);
    }

    return [...locationOptions, ...fetched];
}

function shouldFetchLocationsFromFacebook(locationsToFetch: FacebookTargetingLocationsResponse): boolean {
    const values = Object.values(locationsToFetch);
    return values.some((locations) => locations?.length);
}

async function mapLocationsToOptionsFromFacebook(
    locations: FacebookTargetingLocationsResponse,
    requestInit?: RequestInit
): Promise<FacebookTargetingLocationSearchOption[]> {
    type Payload = {
        data: {
            countries: Record<string, FacebookTargetingCountrySearchResult>;
            regions: Record<string, FacebookTargetingRegionSearchResult>;
            cities: Record<string, FacebookTargetingCitySearchResult>;
            zips: Record<string, FacebookTargetingZIPSearchResult>;
            country_groups: Record<string, FacebookTargetingCountryGroupSearchResult>;
        };
    };

    const response = await fetchFacebookApi<Payload>(
        '/search',
        {
            type: 'adgeolocationmeta',
            countries: JSON.stringify(locations.country_objects?.map((c) => c.key) ?? []),
            cities: JSON.stringify(locations.cities?.map((city) => city.key) ?? []),
            regions: JSON.stringify(locations.regions?.map((region) => region.key) ?? []),
            zips: JSON.stringify(locations.zips?.map((zip) => zip.key) ?? []),
            country_group: JSON.stringify(locations.zips ?? []),
        },
        requestInit
    );

    if (isFacebookError(response)) {
        throw response.error;
    }

    return Object.values(response.data)
        .map((locations) => Object.values(locations))
        .flat()
        .map(mapLocationSearchResultToOption)
        .map((option) => {
            switch (option.type) {
                case 'country':
                    return { ...option, country_code: option.value };
                case 'region':
                    return { ...option, ...(locations.regions?.find((region) => region.key === option.value) || {}) };
                case 'geo_market':
                    return {
                        ...option,
                        ...(locations.geo_markets?.find((market) => market.key === option.value) || {}),
                    };
                case 'city':
                case 'subcity':
                case 'large_geo_area':
                case 'medium_geo_area':
                case 'small_geo_area':
                case 'neighborhood':
                    const city = locations.cities?.find((city) => city.key === option.value);
                    return city
                        ? {
                              ...option,
                              radius: {
                                  distance: city.radius,
                                  unit: radiusUnitOptions.find((u) => u.value === city.distance_unit)!,
                              },
                          }
                        : option;
                case 'zip':
                    return { ...option, ...(locations.zips?.find((zip) => zip.key === option.value) || {}) };
                case 'electoral_district':
                    return {
                        ...option,
                        ...(locations.electoral_districts?.find((district) => district.key === option.value) || {}),
                    };
                default:
                    return option;
            }
        });
}

// some default values are set, but usually are rewritten afterwards
// (for example mapping already saved locations from backend)
export function mapLocationSearchResultToOption(
    location: FacebookTargetingLocationSearchResult
): FacebookTargetingLocationSearchOption {
    const { region = '', country_code = '' } = location as FacebookTargetingCitySearchResult;
    const label = [location.name, region, country_code].filter((value) => !!value).join(', ');
    const locationOption: FacebookTargetingLocationSearchOption = {
        type: location.type,
        typeLabel: formatSnakeCaseToDisplay(location.type),
        label: label,
        value: location.key,
        isIncluded: true,
        name: location.name,
        country_code,
        region,
    };

    if (isFacebookTargetingLocationOptionsWithRadius(locationOption)) {
        locationOption.radius = {
            distance: 18,
            unit: radiusUnitOptions.find((o) => o.value === 'kilometer')!,
        };
    }

    return locationOption;
}
